import { ReactNode } from 'react';

import Konva from 'konva';

export interface IToolItem {
  icon: ReactNode;
  tool: TToolType;
  tooltip: string;
  isDividerBefore?: boolean;
  isDividerAfter?: boolean;
  onClick?: () => void;
}

export type TToolType =
  | 'none'
  | 'select'
  | 'pen'
  | 'line'
  | 'ephemeralPen'
  | 'text'
  | 'sticky'
  | 'sticker'
  | 'shapes'
  | 'colorPalette'
  | 'clearAll'
  | 'eraser'
  | 'invite'
  | 'save';

export enum EMagicPenColor {
  BLACK = '#000000',
  GREEN = '#00ff00',
  RED = '#ff0000',
  BLUE = '#0000ff',
  YELLOW = '#ffff00',
  WHITE = '#ffffff',
}

export enum EMagicPenShape {
  RECTANGLE = 'rectangle',
  LINE = 'line',
  ARROW = 'arrow',
  CIRCLE = 'circle',
  STICKER = 'sticker',
  TEXT_BOX = 'text',
  STICKY_NOTE = 'stickyNote',
}

export interface ICreatedBy {
  peer_id: string;
  peer_uuid: string;
  peer_name: string;
  record_id: string;
}

export interface ICanvasObject {
  id: string;
  type: EMagicPenShape;
  points?: number[];
  stroke?: string;
  strokeWidth?: number;
  opacity?: number;
  createdBy: ICreatedBy;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  content?: string;
  drawEndTime?: number;
  ttlMs?: number;
  text?: string;
  fontSize?: number;
  fill?: string;
  deletedAt?: number;
  backgroundColor?: string;
  textColor?: string;
  isEditing?: boolean;
  hasSynced?: boolean;
  /**
   * Rotation angle in degrees applied by the transformer (clockwise).
   * Stored so other peers can replay rotations consistently.
   */
  rotation?: number;
  /**
   * Horizontal scaling factor
   * Primarily used by stickers so width-only resizes persist across clients.
   */
  scaleX?: number;
  /**
   * Vertical scaling factor
   * Included for completeness; current sticker implementation keeps it at 1.
   */
  scaleY?: number;
  /**
   * Z-index for object layering - higher values are rendered on top
   * Used to maintain consistent object ordering across all peers
   */
  zIndex?: number;

  // Relative positioning properties for responsive behavior
  relativeX?: number;
  relativeY?: number;
  relativeWidth?: number;
  relativeHeight?: number;
  relativePoints?: number[];
}

// Define the ref interface
export interface IMagicPenCanvasRef {
  stageRef: React.RefObject<Konva.Stage>;
}

export type TSaveModalTriggerSource = 'toolbar' | 'popper';

export interface IEditingStickyNotes {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  content: string;
  fontSize: number;
  textColor?: string;
}

export enum EMagicPenSockets {
  MAGIC_PEN = 'magicPen',
  FETCH_ANNOTATES = 'magicPenAnnotations',
  HIDE_MAGIC_PEN = 'hideMagicPen',
}

export enum EMagicPenActionType {
  ADD_ANNOTATE = 'addAnnotate',
  UPDATE_ANNOTATE = 'updateAnnotate',
  DELETE_ANNOTATION = 'deleteAnnotation',
  CLEAR_ALL_ANNOTATION = 'clearAllAnnotation',
}

export type TDeleteActionPayload = {
  actionType: EMagicPenActionType.DELETE_ANNOTATION;
  annotationIds: string[];
};

export type TAnnotationPayload = {
  actionType: Exclude<EMagicPenActionType, EMagicPenActionType.DELETE_ANNOTATION>;
  annotations: ICanvasObject[];
};

export type TClearAllActionPayload = {
  actionType: EMagicPenActionType.CLEAR_ALL_ANNOTATION;
  clearOnlyMine: boolean;
};

export type TMagicPenPayload = TDeleteActionPayload | TAnnotationPayload | TClearAllActionPayload;
